import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { get, set, action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type Model from '@ember-data/model';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { dropTask } from 'ember-concurrency';
import FeatureOrUpgradeButton from 'garaje/components/feature-or-upgrade-button';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import featureEnabled from 'garaje/helpers/feature-enabled';
import type Location from 'garaje/models/location';
import type NotificationPreference from 'garaje/models/notification-preference';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import type PluginPreference from 'garaje/types/plugin-preference';
import { SLACK_V2_PLUGIN_KEY } from 'garaje/utils/enums';

interface IntegrationsToggleComponentSignature {
  Args: {
    title: string;
    location: Location;
    pluginPreferences?: PluginPreference[];
  };
}

export default class IntegrationsToggleComponent extends Component<IntegrationsToggleComponentSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;

  @tracked title;

  slackV2PluginKey = SLACK_V2_PLUGIN_KEY;

  constructor(owner: unknown, args: IntegrationsToggleComponentSignature['Args']) {
    super(owner, args);
    this.title = this.args.title ?? 'Integrations';
  }

  get canAccessCall(): boolean {
    // eslint-disable-next-line ember/no-get
    return Boolean(get(this.args.location, 'company.callEnabled'));
  }

  get canAccessSlack(): boolean {
    return this.state.vrSubscription!.canAccessSlack;
  }

  get hasOldSlack(): boolean {
    // eslint-disable-next-line ember/no-get
    return !isEmpty(get(this.args.location, 'company.slackIntegration.id'));
  }

  get newSlack(): PluginPreference[] {
    return this.args.pluginPreferences?.filter((preference) => preference.plugin.key === this.slackV2PluginKey) ?? [];
  }

  get hasNewSlack(): boolean {
    return !isEmpty(this.newSlack);
  }

  get hasSlackEnabled(): boolean {
    return this.hasOldSlack || this.hasNewSlack;
  }

  saveTask = dropTask(async (property: string, value: unknown, model: Model) => {
    model = model || this.args.location;
    // @ts-expect-error - dynamic property setting
    set(model, property, value);

    try {
      await model.save();

      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (_e) {
      model.rollbackAttributes();
    }
  });

  @action
  changeValue(model: NotificationPreference, property: string, value: unknown): boolean {
    void this.saveTask.perform(property, value, model);

    return true;
  }

  @action
  handleLocationToggle(property: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    void this.saveTask.perform(property, target.checked, this.args.location);
  }

  <template>
    {{#if this.title}}
      <h2 class="mt-16 pt-4">{{this.title}}</h2>
    {{/if}}

    {{#if (featureEnabled "host-notification-plugins-enabled")}}
      {{#each @pluginPreferences as |pluginPreference|}}
        <SettingsPanel data-test-host-notification-plugin={{pluginPreference.plugin.key}} as |panel|>
          <panel.header
            {{! @glint-expect-error: iconSmall may return SafeString }}
            @iconUri={{pluginPreference.plugin.iconSmall}}
            @title={{pluginPreference.plugin.name}}
            @description={{pluginPreference.plugin.description}}
            as |header|
          >
            <header.controls>
              <label class="toggle">
                <input
                  type="checkbox"
                  data-test-toggle
                  checked={{pluginPreference.preference.preferenceValue}}
                  {{on
                    "click"
                    (pick "target.checked" (fn this.changeValue pluginPreference.preference "preferenceValue"))
                  }}
                  disabled={{this.saveTask.isRunning}}
                />
                <span></span>
              </label>
            </header.controls>
          </panel.header>
        </SettingsPanel>
      {{/each}}
    {{else}}
      {{#if this.canAccessCall}}
        <SettingsPanel data-test-call-config as |panel|>
          <panel.header
            @iconUri="/assets/images/features/call.svg"
            @title="Phone call"
            @description="Call an employee's phone when their visitor arrives."
            as |header|
          >
            <header.controls>
              <label class="toggle">
                <input
                  type="checkbox"
                  data-test-call-notification-toggle
                  checked={{@location.callNotificationEnabled}}
                  {{on "click" (fn this.handleLocationToggle "callNotificationEnabled")}}
                  disabled={{this.saveTask.isRunning}}
                />
                <span></span>
              </label>
            </header.controls>
          </panel.header>
        </SettingsPanel>
      {{/if}}
      {{#if this.hasSlackEnabled}}
        <SettingsPanel data-test-slack-config as |panel|>
          <panel.header
            @iconUri="/assets/images/features/slack.png"
            @title="Slack"
            @description="Send employees a direct message when their visitor arrives."
            as |header|
          >
            <header.controls>
              <FeatureOrUpgradeButton
                @isAuthorized={{this.canAccessSlack}}
                @featureName="slack"
                @isSlack={{false}}
                @level="standard"
                @logCTAEvents={{true}}
                @shouldCheckMinLevel={{false}}
                @upgradeButtonText="Upgrade"
                @upgradeClass=""
                @upgradeQueryParams={{hash plan="premium" period="monthly"}}
                @vrOnly={{false}}
              >
                <label class="toggle">
                  <input
                    type="checkbox"
                    data-test-slack-notification-toggle
                    checked={{@location.slackNotificationEnabled}}
                    {{on "click" (fn this.handleLocationToggle "slackNotificationEnabled")}}
                    disabled={{this.saveTask.isRunning}}
                  />
                  <span></span>
                </label>
              </FeatureOrUpgradeButton>
            </header.controls>
          </panel.header>
        </SettingsPanel>
      {{/if}}
    {{/if}}
  </template>
}
